import s from './EditApp.module.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Button,
  Form,
  FormItem,
  Header,
  Input,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { goBack } from '$navigation/helper';
import { AppModel } from '$store/models';
import { Loader } from '$shared/components';
import { useTranslator } from '$hooks';
import { APP } from '$shared/constants/pages';
import { IcCopy28 } from '$assets';
import copyToClipboard from 'copy-text-to-clipboard';

export const EditApp: FC<{ appId: number }> = ({ appId }) => {
  const dispatch = useDispatch();
  const t = useTranslator();

  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');

  const loadData = useCallback(() => {
    setLoadingStatus('loading');
    Api.get<any, { app: AppModel; appSecret: string }>(`/app/${appId}?manage=1`)
      .then((resp) => {
        setName(resp.app.name);
        setUrl(resp.app.originalUrl);
        setSecret(resp.appSecret);
        setLoadingStatus('');
      })
      .catch((err) => {
        setLoadingStatus('failed');
        dispatch(toastActions.setToastFail(err.message));
      });
  }, []);

  const handleCopySecret = () => {
    copyToClipboard(secret);
    dispatch(toastActions.setToastSuccess(t('copied')));
  };

  useEffect(() => {
    loadData();
  }, [appId]);

  const handleSave = useCallback(() => {
    dispatch(toastActions.setToastLoading());
    Api.post(`/app/${appId}/save`, {
      name: name.trim(),
      url: url.trim(),
    })
      .then(() => {
        dispatch(toastActions.hideToast());
        goBack();
      })
      .catch((err) => {
        dispatch(toastActions.setToastFail(err.message));
      });
  }, [appId, name, url]);

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <Form>
        <FormItem>
          <List>
            <ListItem chevron routeName={APP} routeParams={{ id: appId }}>
              Open app
            </ListItem>
          </List>
        </FormItem>
        <FormItem title="Name">
          <Input
            placeholder="Enter name..."
            value={name}
            onChange={({ target }) => setName(target.value)}
          />
        </FormItem>
        <FormItem title="Url">
          <Input
            placeholder="Enter url..."
            value={url}
            onChange={({ target }) => setUrl(target.value)}
          />
        </FormItem>
        <FormItem title="Secret">
          <div className={s.secretWrap} onClick={handleCopySecret}>
            <Input value={secret.slice(0, 4) + '...' + secret.slice(-4)} disabled />
            <div className={s.secretCopy}>
              <IcCopy28 />
            </div>
          </div>
        </FormItem>
        <FormItem>
          <Button onClick={handleSave}>Save</Button>
        </FormItem>
      </Form>
    );
  }

  return (
    <>
      <Header showCloseButton>New App</Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
